import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Upload } from "lucide-react";
import { API } from "../../App";
import { PhotoPicker } from "./ContractorCompleteDialog";
import { managerPhotoUrl, uploadCompletionPhotos, deleteCompletionPhoto } from "./completionPhotoApi";
import { AuthImage } from "./AuthImage";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const PhotoTile = ({ defectId, p, approvable, approved, onToggle, onRemove }) => (
  <div className="rounded-lg border border-slate-200 p-2 space-y-1.5 w-[calc(50%-0.25rem)] sm:w-36" data-testid={`closeout-photo-${p.id}`}>
    <AuthImage src={managerPhotoUrl(defectId, p.id)} alt="Completion" className="w-full aspect-square rounded-md object-cover" />
    <p className="text-[11px] text-slate-500 truncate">{p.uploaded_by === "contractor" ? "Contractor" : "Manager"} · {p.uploaded_by_name}</p>
    {approvable && (
      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer">
        <Checkbox checked={approved} onCheckedChange={(v) => onToggle(p.id, !!v)} data-testid={`closeout-approve-${p.id}`} />Show to client
      </label>
    )}
    <button type="button" onClick={() => onRemove(p)} className="text-[11px] text-red-600 hover:underline" data-testid={`closeout-remove-${p.id}`}>Remove</button>
  </div>
);

export const CloseOutDialog = ({ defect, onOpenChange, onDone }) => {
  const [photos, setPhotos] = useState([]);
  const [approved, setApproved] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!defect) return;
    setPhotos(defect.completion_photos || []);
    setApproved([]);
    setFiles([]);
  }, [defect]);

  if (!defect) return null;
  const base = `${API}/defects/${defect.id}/completion-photos`;
  const clientItem = !!defect.client_visible;

  const upload = async () => {
    setBusy(true);
    try {
      const d = await uploadCompletionPhotos(base, files, auth());
      setPhotos(d.completion_photos || []);
      setFiles([]);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm("Remove this completion photo?")) return;
    try {
      setPhotos((await deleteCompletionPhoto(`${base}/${p.id}`, auth())).completion_photos || []);
      setApproved((a) => a.filter((x) => x !== p.id));
    } catch (e) { toast.error(e.message); }
  };
  const closeOut = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/defects/${defect.id}/close-out`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify({ approved_photo_ids: approved }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Failed to close out defect");
      toast.success(clientItem && approved.length ? `Closed out — ${approved.length} photo(s) shared with the client` : "Defect closed out successfully");
      onDone();
      onOpenChange(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!defect} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="closeout-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-green-600" />Close Out {defect.number}</DialogTitle>
          <DialogDescription>
            {defect.area} — {defect.element}: {defect.issue}.{" "}
            {clientItem ? "Tick the completion photos the client may see." : "This is an internal defect — photos stay internal."}
          </DialogDescription>
        </DialogHeader>
        {photos.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {photos.map((p) => (
              <PhotoTile key={p.id} defectId={defect.id} p={p} approvable={clientItem} approved={approved.includes(p.id)}
                onToggle={(id, v) => setApproved((a) => (v ? [...a, id] : a.filter((x) => x !== id)))} onRemove={remove} />
            ))}
          </div>
        ) : <p className="text-sm text-slate-500" data-testid="closeout-no-photos">No completion photos yet.</p>}
        {photos.length < 3 && (
          <div className="space-y-2 rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-semibold text-slate-600">Add your own completion photos</p>
            <PhotoPicker files={files} setFiles={setFiles} max={3 - photos.length} testId="closeout-add-photo" />
            {files.length > 0 && (
              <Button size="sm" variant="outline" onClick={upload} disabled={busy} data-testid="closeout-upload-button">
                <Upload className="w-3.5 h-3.5 mr-1" />Upload {files.length} photo{files.length === 1 ? "" : "s"}
              </Button>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="closeout-cancel">Cancel</Button>
          <Button onClick={closeOut} disabled={busy || files.length > 0} className="bg-green-600 hover:bg-green-700 text-white" data-testid="closeout-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Close Out"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
